import { Account, Client, Functions, ExecutionMethod } from 'appwrite';
import { getTemplate, listTemplates, type SqlReport } from './registry';
import type { SellerReport } from './templates/seller-company/schema';
import type { CampaignReport } from './schema/campaign';

export interface ReportResult { success: boolean; reportId: string; bucket?: 'lead-reports' | 'campaign-report-exports'; status: 'ready'; url?: string; error?: string; message?: string; provider?: string; model?: string; cached?: boolean }
export interface TranslateResult { success: boolean; translations?: Record<string, string>; error?: string; remaining_credits?: number; provider?: string; model?: string }
export interface SaveResult { success: boolean; error?: string }

const client = new Client().setEndpoint(import.meta.env.VITE_APPWRITE_ENDPOINT || 'https://localhost/v1').setProject(import.meta.env.VITE_APPWRITE_PROJECT_ID || 'unconfigured');
const functions = new Functions(client);
export async function reportAccess(): Promise<boolean> {
  try { const user = await new Account(client).get(); return true; } catch { return false; }
}
async function proxy(action: string, parameters: Record<string, unknown>): Promise<any> {
  console.log(`[reports] ${action} → executing…`);
  const execution = await functions.createExecution('n8n-proxy', JSON.stringify({ action, parameters }), false, '/', ExecutionMethod.POST);
  console.log(`[reports] ${action} ← ${execution.responseStatusCode}`, (execution.responseBody || '').slice(0, 200));
  let result: any;
  try { result = JSON.parse(execution.responseBody); } catch { throw new Error('The report service returned an unreadable response.'); }
  if (execution.responseStatusCode >= 400 || !result?.success) throw new Error(result?.error || 'The report service is unavailable.');
  return result;
}
export const listReportTemplates = listTemplates;
/**
 * REUSABLE AI translation: translates a flat texts block via the
 * ai-translate function (Gemini Flash Lite default; provider/model selectable
 * from the Admin Panel). Usable by any feature, independent of reports.
 */
export const translateTexts = (templateId: string, texts: Record<string, string>, language: string, options?: { provider?: string; model?: string }): Promise<TranslateResult> =>
  proxy('report_translate', { templateId, language, texts, ...options }) as Promise<TranslateResult>;
export const translateReport = (templateId: string, report: SqlReport | CampaignReport | SellerReport, language: string): Promise<TranslateResult> => {
  const template = getTemplate(templateId);
  if (!template) return Promise.reject(new Error(`Unknown template: ${templateId}`));
  return translateTexts(templateId, template.translationTexts(report), language);
};
/**
 * Publish: converts the client-rendered HTML to PDF (Gotenberg, server-side)
 * and stores it at the deterministic file id. Existing artifacts are returned
 * instantly (cached), so re-opening or sharing never re-converts.
 */
export const publishReport = (templateId: string, report: SqlReport | CampaignReport | SellerReport, language: string, html: string) =>
  proxy('report_publish', { templateId, language, report, html }) as Promise<ReportResult>;
export const getReportStatus = (reportId: string, bucket: 'lead-reports' | 'campaign-report-exports') => proxy('report_status', { reportId, bucket }) as Promise<ReportResult>;
/**
 * Save: hands the finished report to n8n with FULL context — reportType,
 * language, source ids, URL — so n8n knows the client, user, lead, campaign
 * and persists the link on the source record.
 */
export const saveReportRecord = (params: { sourceType: 'lead' | 'campaign'; sourceId: string; campaignId?: string; templateId: string; language: string; fileUrl: string; fileId?: string; context?: Record<string, string> }) =>
  proxy('report_save', params) as Promise<SaveResult>;


// ── Template section config (admin-managed) ─────────────────────────────────
import { Databases } from 'appwrite';
import { type SectionConfig, normalizeTemplateConfigs, validateTemplateConfigs } from './section-catalog';

const databases = new Databases(client);
const SETTINGS_DB = import.meta.env.VITE_APPWRITE_DATABASE_ID || '693c1ae3003d56af4154';
const SETTINGS_COLLECTION = 'platform_settings';
const CONFIG_DOC_ID = 'report_template_config';

/** Fetch the section config for all templates. Returns DEFAULT_CONFIG if unset. */
export async function getTemplateConfig(): Promise<Record<string, SectionConfig>> {
  try {
    const doc = await databases.getDocument(SETTINGS_DB, SETTINGS_COLLECTION, CONFIG_DOC_ID);
    const raw = JSON.parse(doc.setting_value || '{}');
    return normalizeTemplateConfigs(raw);
  } catch (error: any) {
    if (error?.code === 404) return normalizeTemplateConfigs({});
    throw error;
  }
}

/** Save the section config (superadmin only — enforced by collection permissions). */
export async function saveTemplateConfig(config: Record<string, SectionConfig>): Promise<void> {
  const merged = { ...await getTemplateConfig(), ...config };
  validateTemplateConfigs(merged);
  const json = JSON.stringify(normalizeTemplateConfigs(merged));
  const data = { setting_key: CONFIG_DOC_ID, setting_value: json };
  try {
    await databases.updateDocument(SETTINGS_DB, SETTINGS_COLLECTION, CONFIG_DOC_ID, data);
  } catch (e: any) {
    // Only a genuine "not found" justifies creating; any other error is a real
    // failure (permissions, validation) and must surface rather than be masked
    // by a second, equally-doomed create attempt.
    if (e?.code !== 404) throw e;
    try {
      await databases.createDocument(SETTINGS_DB, SETTINGS_COLLECTION, CONFIG_DOC_ID, data);
    } catch (createError: any) {
      // Concurrent first-save race: another request created it between our
      // update (404) and create (409) — the update is now the right call.
      if (createError?.code !== 409) throw createError;
      await databases.updateDocument(SETTINGS_DB, SETTINGS_COLLECTION, CONFIG_DOC_ID, data);
    }
  }
}
